import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Plus, Wand2, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useLang } from "../i18n";

export function RevisitCallout({ coreEmotion, subject, testId = "revisit-callout" }) {
  const { lang } = useLang();
  if (!coreEmotion) return null;
  const text = lang === "nl"
    ? `In het licht van de kernemotie "${coreEmotion}", moet ${subject} nog worden aangepast?`
    : `In light of the core emotion "${coreEmotion}", should ${subject} still be adjusted?`;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={testId}
      className="flex items-start gap-3 rounded-2xl border-2 border-accent/40 bg-accent/10 p-4 sm:p-5">
      <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
      <p className="text-sm font-semibold leading-relaxed text-[#8a5200]">{text}</p>
    </motion.div>
  );
}

export function PhaseIntro({ phase, title, intro, badge }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-primary/15 bg-gradient-to-br from-secondary to-card p-6 sm:p-8">
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold uppercase tracking-widest text-accent">Fase {phase}</span>
        {badge}
      </div>
      <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-primary sm:text-3xl">{title}</h2>
      <p className="mt-3 max-w-3xl text-base leading-relaxed text-muted-foreground">{intro}</p>
    </motion.div>
  );
}

export function AddInput({ placeholder, onAdd, testId, label }) {
  const [v, setV] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!v.trim()) return;
    onAdd(v.trim());
    setV("");
  };
  return (
    <form onSubmit={submit} className="flex gap-2">
      <Input data-testid={testId} value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} className="rounded-full" />
      <Button type="submit" data-testid={`${testId}-add`} className="shrink-0 rounded-full">
        <Plus className="mr-1 h-4 w-4" /> {label}
      </Button>
    </form>
  );
}

export function AiButton({ onClick, remaining, busy, label, testId }) {
  const disabled = remaining <= 0 || busy;
  return (
    <Button data-testid={testId} onClick={onClick} disabled={disabled} variant="outline"
      className="rounded-full border-accent/40 bg-accent/5 text-accent hover:bg-accent hover:text-accent-foreground">
      <Sparkles className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} />
      {label}
      <span className="ml-2 rounded-full bg-accent/15 px-2 py-0.5 text-xs font-bold">{Math.max(0, remaining)}×</span>
    </Button>
  );
}

export function ReclusterButton({ onClick, busy, label, testId }) {
  return (
    <Button data-testid={testId} onClick={onClick} disabled={busy} variant="ghost" size="sm" className="rounded-full text-primary">
      <Wand2 className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {label}
    </Button>
  );
}
